import React, { useRef, useEffect, useState } from 'react';
import { find } from 'lodash';
import moment from 'moment-timezone';

import { SparkleIcon } from '@magoosh/lib/icons';

import NoContent from '../no_content';
import AnnotationPopover from './annotation_popover';
import { Annotation, EditedResponseFilter } from '../../types';

import MagooshAI from '../assets/magoosh_ai.svg';
import style from './style.module.scss'

interface Props {
  activeFilters: EditedResponseFilter[];
  editedResponse: {
    content: string;
    diff: string;
    score: number;
  };
  examName: string;
  filters: EditedResponseFilter[];
  gradedAt: number;
  onChangeFilters: (filters: EditedResponseFilter[]) => void;
  score: number;
  status: 'waiting' | 'processing' | 'finished' | 'error';
}

export default (props: Props) => {
  if (props.status === 'finished' && props.examName === 'GRE' && props.score >= 5) {
    return <NoContent message="You already have a great score and we weren't able to generate suggested edits this time." />;
  } else if (!props.editedResponse && props.status === 'finished') {
    return <NoContent message="We weren't able to generate suggested edits this time." />;
  } else if (!props.editedResponse) {
    return <NoContent />;
  }

  const diffRef = useRef<HTMLDivElement>(null);
  const [activeAnnotation, setActiveAnnotation] = useState<Annotation>(null);

  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles';

  const filteredEssayDiff = () => {
    let diff = props.editedResponse.diff;

    props.activeFilters.forEach((filter) => {
      const regex = new RegExp(`class=\"${filter.value}\"`, 'g');
      diff = diff.replace(regex, `class="${filter.value} active"`);
    });

    return diff;
  };

  // Some <ins> elements in the diff HTML will have a data-annotation and data-annotation-index,
  // which indicates there is addition context we can display in an overlay. Number the
  // annotations in the UI and handle a click to display the annotation details.
  useEffect(() => {
    if (diffRef.current) {
      const elementsWithAnnotations = diffRef.current.querySelectorAll('.active[data-annotation-index]');

      elementsWithAnnotations.forEach((element: HTMLElement) => {
        const annotationIndex = element.getAttribute('data-annotation-index');

        if (annotationIndex && !element.nextElementSibling?.classList.contains('annotation-index')) {
          const span = document.createElement('span');
          span.className = 'annotation-index';
          span.textContent = annotationIndex;
          element.insertAdjacentElement('afterend', span);
          element.style.cursor = 'pointer';

          // On click, set the active, visible annotation details
          element.addEventListener('click', (e) => {
            e.preventDefault();
            setActiveAnnotationFromElement(element);
          });
        }
      });
    }
  }, [filteredEssayDiff()]);

  const clearActiveAnnotationClasses = () => {
    document.querySelectorAll('.active-annotation').forEach((element) => (
      element.classList.remove('active-annotation')
    ));
  };

  const handleNextAnnotation = () => {
    if (!activeAnnotation || !diffRef.current) return;

    // Find the next element with active class and data-annotation-index following the current one
    let nextElement = activeAnnotation.element.nextElementSibling;

    while (nextElement) {
      if (nextElement.classList.contains('active') && nextElement.hasAttribute('data-annotation-index')) {
        setActiveAnnotationFromElement(nextElement);
        return;
      }
      nextElement = nextElement.nextElementSibling;
    }

    // If no next element found, wrap around to first annotation
    makeFirstAnnotationActive();
  };

  const hasAnnotations = () => {
    return props.editedResponse.diff.indexOf('data-annotation') !== -1;
  };

  const makeFirstAnnotationActive = () => {
    props.onChangeFilters(props.filters);

    // Small timeout needed so that the content of the newly active filters get back in the props
    setTimeout(() => {
      const firstElement = diffRef.current.querySelector('.active[data-annotation-index]') as HTMLElement;
      if (firstElement) {
        setActiveAnnotationFromElement(firstElement);
      }
    }, 250);
  };

  const setActiveAnnotationFromElement = (element) => {
    clearActiveAnnotationClasses();
    element.classList.add('active-annotation');

    const category = find(
      props.filters,
      (filter) => element.classList.contains(filter.value)
    ) as EditedResponseFilter;
    setActiveAnnotation({
      category: category?.name,
      content: element.getAttribute('data-annotation'),
      element,
      index: parseInt(element.getAttribute('data-annotation-index'))
    });
  };

  return (
    <>
      {
        hasAnnotations() && (
          <>
            <div className="align-items-md-center d-flex flex-column flex-md-row gap-s">
              <div className="d-flex flex-none">
                <div
                  className="align-items-center d-flex flex-none gap-xxs"
                  onClick={makeFirstAnnotationActive}
                  style={{
                    background: 'linear-gradient(90deg, #0577E9, #5227F8)',
                    borderRadius: 24,
                    boxShadow: '0px 2px 2px -1px #0000000D, 0px 5px 5px -2.5px #0000000D, 0px 0px 0px 1px #0000001A',
                    color: '#fff',
                    cursor: 'pointer',
                    padding: '8px 15px'
                  }}
                >
                  <SparkleIcon color="#fff" size={18} />
                  <div><strong>Start Review</strong></div>
                </div>
              </div>

              <strong>We’ll walk you through each edit and why it was made.</strong>
            </div>

            <hr className="u-margin-A-xxs" />
          </>
        )
      }

      <div className="align-items-center d-flex gap-s">
        <img alt="" src={MagooshAI} />

        <div className="small">
          Magoosh AI Tutor
          {' '} on {moment.unix(props.gradedAt).tz(timeZone).format('MMMM D, YYYY')}
        </div>
      </div>

      <div className="d-flex flex-column" style={{ gap: 20 }}>
        <div
          className={style.editedEssay}
          dangerouslySetInnerHTML={{ __html: filteredEssayDiff() }}
          ref={diffRef}
        />
      </div>

      {
        activeAnnotation && (
          <AnnotationPopover
            content={activeAnnotation.content}
            category={activeAnnotation.category}
            element={activeAnnotation.element}
            index={activeAnnotation.index}
            onClose={() => {
              clearActiveAnnotationClasses();
              setActiveAnnotation(null);
            }}
            onNext={handleNextAnnotation}
          />
        )
      }
    </>
  );
};
